import levelup, { LevelUp } from 'levelup';
import { isNil, isObject } from 'lodash';
import memdown from 'memdown';

export const singletonEnforcer = Symbol('singletonEnforcer');

export default class Store {
  public static async get<T = unknown, D = T | undefined>(key: string, defaultValue?: D): Promise<T | D> {
    return Store.instance.get<T, D>(key, defaultValue);
  }

  public static async set<T = unknown>(key: string, value: T): Promise<void> {
    return Store.instance.set(key, value);
  }

  public static async delete(key: string): Promise<void> {
    return Store.instance.delete(key);
  }

  private static singleton: Store;

  private db: LevelUp;

  /**
  * Represents an instance of the application configurations handler
  * @constructor
  * */
  constructor(enforcer: symbol) {
    if (enforcer !== singletonEnforcer) { throw new Error('Cannot construct singleton'); }
    this.db = levelup(memdown());
  }

  /**
  * Returns the current instance
  * @return { Store } - Current Store instance
  * */
  static get instance(): Store {
    if (!Store.singleton) {
      Store.singleton = new Store(singletonEnforcer);
    }
    return Store.singleton;
  }

  public async get<T = unknown, D = T | undefined>(key: string, defaultValue?: D): Promise<T | D> {
    try {
      const val = await this.db.get(key, { asBuffer: false });
      let value: T;
      try {
        value = JSON.parse(val);
      } catch {
        value = val;
      }
      if (isNil(value)) {
        return defaultValue;
      }
      return value;
    } catch {
      return defaultValue;
    }
  }

  public async set<T = unknown>(key: string, value: T): Promise<void> {
    const valueToSave = (isObject(value)) ? JSON.stringify(value) : value;
    return this.db.put(key, valueToSave);
  }

  public async delete(key: string): Promise<void> {
    return this.db.del(key);
  }
}
